import { QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Toaster } from "@altek/ui/components/feedback/Toaster";
import { TooltipProvider } from "@altek/ui/components/ui/Tooltip";
import { queryClient } from "@/lib/api/query-client";
import { AppErrorBoundary } from "./AppErrorBoundary";
import { AuthProvider } from "./AuthProvider";
import { ThemeProvider } from "@altek/ui/components/theme/ThemeProvider";

/**
 * The provider chain, in the one order that works.
 *
 * ```
 * AppErrorBoundary → QueryClientProvider → AuthProvider → ThemeProvider → { router, Toaster }
 * ```
 *
 * Why this order:
 *
 *   `AppErrorBoundary` outermost — a provider that throws while initialising takes everything
 *   below it down, so the boundary cannot be one of those things.
 *
 *   `QueryClientProvider` above everything that reads the API, and `AuthProvider` directly
 *   below it, because the session is a query like any other. Scope needs no provider at all —
 *   it lives in the URL, inside the router, which keeps a shared link honest.
 *
 *   `AuthProvider` OUTSIDE the router, not inside a layout route. The sign-in page has to know
 *   whether somebody is already signed in, and a session read that only exists inside the
 *   authenticated layout cannot answer that — it would show the form to a signed-in user on
 *   every reload.
 *
 *   `ThemeProvider` above the visual tree, so the resolved theme is on the document before the
 *   first component paints. `index.html` already stamped it inline; this takes over afterwards.
 *
 *   `Toaster` a sibling of the router, not a child of a route: a toast raised by a mutation
 *   must survive the navigation that mutation triggers.
 *
 * HARD RULE: no provider performs a side effect on mount beyond reading the session. First
 * render stays light — every millisecond here is in front of the first paint.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            {/**
             * App-wide, not per-surface. `Tooltip` throws outside a provider rather than
             * degrading, and the shell's own rail uses one on every row it collapses and on
             * every disabled row's reason — so the crash lands on the whole application, not
             * on the tooltip. Mounting it here means no surface has to remember.
             */}
            <TooltipProvider>
              {children}
              <Toaster />
            </TooltipProvider>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
